import React, { useState, useEffect, useCallback } from "react";
import Styles from "./CompanyDetails.module.css";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAdminConsumerRaListing,
  fetchConsumerCapacities,
  clearActivities,
} from "../../../Redux/Slices/gnaAdminSlice";
import { clearWalletContract } from "../../../Redux/Slices/paymentSlice";
import ListingDetails from "../../Modals/Admin/ListingDetails/ListingDetails";
import ListingTable from "./ListingTable";
import SchdeuleTable from "./SchdeuleTable";
import WalletTable from "./WalletTable/WalletTable";

const ConsumerDetails = ({ fullConsumerDetails }) => {
  const dispatch = useDispatch();
  const role = useSelector(
    (state) => state?.persistedReducer?.user?.activeRole,
  );
  const consumerId = fullConsumerDetails?.consumerId;
  const stateLabel = fullConsumerDetails?.stateUtId;

  const [viewType, setViewType] = useState("details");
  const [listingView, setListingView] = useState({ open: false, data: null });

  const bilateralListings = useSelector(
    (state) => state.gnaAdminSlice.fetchCapacity?.data?.page,
  );
  const raListings = useSelector(
    (state) => state.gnaAdminSlice.fetchRaList?.data?.page,
  );

  useEffect(() => {
    dispatch(clearActivities());
    dispatch(clearWalletContract());
  }, [consumerId, dispatch]);

  useEffect(() => {
    if (!consumerId) return;
    switch (viewType) {
      case "bilateral":
        dispatch(fetchConsumerCapacities(consumerId));
        break;
      case "ra":
        dispatch(fetchAdminConsumerRaListing({ id: consumerId, type: "RA" }));
        break;
      case "rfp_intrastate":
        dispatch(
          fetchAdminConsumerRaListing({
            id: consumerId,
            type: "CB",
            auctionType: "CLOSED",
          }),
        );
        break;
      case "rfp_interstate":
        dispatch(
          fetchAdminConsumerRaListing({
            id: consumerId,
            type: "CB",
            auctionType: "OPEN",
          }),
        );
        break;
      default:
        break;
    }
  }, [viewType, consumerId, dispatch]);

  const renderSchedule = useCallback(
    () => (
      <SchdeuleTable
        id={consumerId}
        companyId={fullConsumerDetails?.companyId}
      />
    ),
    [consumerId, fullConsumerDetails?.companyId],
  );

  const renderDetails = useCallback(() => {
    const wallet = fullConsumerDetails?.walletAccount?.displayAmountRemaining;
    const bank = fullConsumerDetails?.consumerBankDetails;
    const office = fullConsumerDetails?.consumerOfficeAddress;

    return (
      <div className={Styles.entityDetailSections}>
        {/* Consumer Information */}
        <div className={Styles.secCard}>
          <div className={Styles.secCardInner}>
            <div className={Styles.secCardMain}>
              <div className={Styles.secTitle}>Consumer Information</div>
              <div className={Styles.secFields}>
                <div className={Styles.field}>
                  <span className={Styles.fieldLabel}>Consumer ID</span>
                  <span className={Styles.fieldValue}>
                    {fullConsumerDetails?.consumerId || "—"}
                  </span>
                </div>
                <div className={Styles.field}>
                  <span className={Styles.fieldLabel}>Name</span>
                  <span className={Styles.fieldValue}>
                    {fullConsumerDetails?.consumerName || "—"}
                  </span>
                </div>
                <div className={Styles.field}>
                  <span className={Styles.fieldLabel}>Type</span>
                  <span className={Styles.fieldValue}>
                    {fullConsumerDetails?.specificationName || "—"}
                  </span>
                </div>
              </div>
            </div>
            {wallet && (
              <div className={Styles.walletChip}>
                ₹{" "}
                {Number(wallet).toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                })}
              </div>
            )}
          </div>
        </div>

        {/* Bank Details */}
        <div className={Styles.secCard}>
          <div className={Styles.secTitle}>Bank Details</div>
          <div className={Styles.secFields}>
            <div className={Styles.field}>
              <span className={Styles.fieldLabel}>Bank Name</span>
              <span className={Styles.fieldValue}>{bank?.bankName || "—"}</span>
            </div>
            <div className={Styles.field}>
              <span className={Styles.fieldLabel}>Account Number</span>
              <span className={`${Styles.fieldValue} ${Styles.fieldMono}`}>
                {bank?.accountNumber || "—"}
              </span>
            </div>
            <div className={Styles.field}>
              <span className={Styles.fieldLabel}>IFSC Code</span>
              <span className={`${Styles.fieldValue} ${Styles.fieldMono}`}>
                {bank?.ifscCode || "—"}
              </span>
            </div>
            <div className={Styles.field}>
              <span className={Styles.fieldLabel}>Payee Name</span>
              <span
                className={`${Styles.fieldValue} ${!bank?.payee ? Styles.fieldMuted : ""}`}
              >
                {bank?.payee || "Not specified"}
              </span>
            </div>
          </div>
        </div>

        {/* Office Address */}
        <div className={Styles.secCard}>
          <div className={Styles.secTitle}>Office Address</div>
          <div className={Styles.secFields}>
            <div className={`${Styles.field} ${Styles.fieldSpan}`}>
              <span className={Styles.fieldLabel}>Address</span>
              <span className={Styles.fieldValue}>
                {office?.address || "—"}
              </span>
            </div>
            <div className={Styles.field}>
              <span className={Styles.fieldLabel}>City</span>
              <span className={Styles.fieldValue}>
                {office?.cityName || "—"}
              </span>
            </div>
            <div className={Styles.field}>
              <span className={Styles.fieldLabel}>Pincode</span>
              <span className={Styles.fieldValue}>
                {office?.pinCode || "—"}
              </span>
            </div>
            <div className={Styles.field}>
              <span className={Styles.fieldLabel}>State</span>
              <span className={Styles.fieldValue}>
                {office?.stateUtName || "—"}
              </span>
            </div>
            <div className={Styles.field}>
              <span className={Styles.fieldLabel}>Mobile</span>
              <span className={Styles.fieldValue}>
                {office?.mobileNumber || "—"}
              </span>
            </div>
            <div className={Styles.field}>
              <span className={Styles.fieldLabel}>Email</span>
              <span className={`${Styles.fieldValue} ${Styles.fieldLink}`}>
                {office?.email || "—"}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }, [fullConsumerDetails]);

  const renderActiveView = () => {
    switch (viewType) {
      case "details":
        return renderDetails();
      case "schedule":
        return renderSchedule();
      case "bilateral":
        return (
          <ListingTable
            type="bilateral"
            data={bilateralListings}
            setListingView={setListingView}
          />
        );
      case "ra":
        return (
          <ListingTable
            type="ra"
            entityRole="consumer"
            data={raListings}
            setListingView={setListingView}
          />
        );
      case "rfp_intrastate":
        return (
          <ListingTable
            type="rfq"
            entityRole="consumer"
            data={raListings}
            setListingView={setListingView}
          />
        );
      case "rfp_interstate":
        return (
          <ListingTable
            type="rfq"
            entityRole="consumer"
            data={raListings}
            setListingView={setListingView}
          />
        );
      case "wallet":
        return <WalletTable fullConsumerDetails={fullConsumerDetails} />;
      default:
        return null;
    }
  };

  const subTabs = [
    { id: "details", label: "Details" },
    ...(role === "MAKER" || role === "CHECKER"
      ? [{ id: "schedule", label: "Payment" }]
      : []),
    { id: "bilateral", label: "Bilateral listings" },
    { id: "ra", label: "Reverse Auctions" },
    { id: "rfp_interstate", label: "RFP(Interstate)" },
    {
      id: "rfp_intrastate",
      label: stateLabel === 24 ? "TradeConnect" : "RFP(Intrastate)",
    },
    { id: "wallet", label: "Transaction details" },
  ];

  return (
    <div className={Styles.entityDetailWrapper}>
      {listingView.open && (
        <ListingDetails
          open={listingView.open}
          handleClose={() => setListingView({ open: false, data: null })}
          data={listingView.data}
        />
      )}
      <div className={Styles.subTabs}>
        {subTabs.map((tab) => (
          <button
            key={tab.id}
            className={`${Styles.subTab} ${viewType === tab.id ? Styles.subTabActive : ""}`}
            onClick={() => setViewType(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className={Styles.entityDetailContent}>{renderActiveView()}</div>
    </div>
  );
};

export default ConsumerDetails;
